import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, UserSearch } from 'lucide-react'
import { useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees } from '@/api/generated/company-management-user/company-management-user'
import type { EmployeeUserResponse, ServiceModuleModel } from '@/api/model'
import { PageLayout } from '@/components/layout/PageLayout'
import { PageHeader } from '@/components/layout/PageHeader'
import { PageHeaderActions } from '@/components/layout/PageHeaderActions'
import { Button } from '@/components/shadcn/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select'
import { FilterRow } from '@/components/common/filters/FilterRow'
import { FilterDateRangePicker } from '@/components/common/filters/FilterDateRangePicker'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { useTimeTrackingEntries } from '../hooks/useTimeTrackingEntries'
import { TimeTrackingEntriesTable } from './TimeTrackingEntriesTable'
import { TimeTrackingApprovalsSection } from './TimeTrackingApprovalsSection'
import { unwrap } from '@/utils/unwrap.ts'

interface Props {
  module: ServiceModuleModel
  onBack: () => void
}

const ALL_EMPLOYEES = 'all'

/**
 * "All time entries" page (header button of the module overview → here). Filter
 * (employee + date range) + server-paginated list with approve/reject.
 * Mirrors `AbsenceAllEntriesPage` — but **without** a calendar (the time tracking
 * module has no calendar view).
 */
export function TimeTrackingAllEntriesPage({ module, onBack }: Props) {
  const { t } = useTranslation(['service-modules-timetracking', 'common', 'filters'])
  const id = module.id

  const [employeeUserId, setEmployeeUserId] = useState<string>()
  const [range, setRange] = useState<{ from?: string; to?: string }>({})

  const employeesQuery = useGetApiCompaniesCurrentServicemodulesServiceModuleIdEmployees(id)
  const employees = useMemo(
    () => unwrap<EmployeeUserResponse[]>(employeesQuery.data) ?? [],
    [employeesQuery.data],
  )

  const entries = useTimeTrackingEntries(id, { employeeUserId, from: range.from, to: range.to })

  return (
    <PageLayout scrollEnabled>
      <PageHeader title={t('timetracking.allTitle')}>
        <PageHeaderActions>
          <Button variant="outline" className="h-9 gap-2" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
            {t('buttons.back', { ns: 'common' })}
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <TimeTrackingApprovalsSection serviceModuleId={id} />

      <h2 className="text-lg font-semibold">{t('timetracking.allEntriesSection')}</h2>
      <FilterRow>
        <Select
          value={employeeUserId ?? ALL_EMPLOYEES}
          onValueChange={(v) => setEmployeeUserId(v === ALL_EMPLOYEES ? undefined : v)}
        >
          <SelectTrigger className="w-56 h-9!">
            <SelectValue placeholder={t('dropdown.employeePlaceholder', { ns: 'filters' })} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_EMPLOYEES}>{t('dropdown.allEmployees', { ns: 'filters' })}</SelectItem>
            {employees.map((employee) => (
              <SelectItem key={employee.id} value={employee.id ?? ''}>
                {`${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || (employee.id ?? '—')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setEmployeeUserId(undefined)}>
              <UserSearch className="h-4 w-4 text-red-400" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('dropdown.showAllEmployees', { ns: 'filters' })}</TooltipContent>
        </Tooltip>

        <FilterDateRangePicker from={range.from} to={range.to} onChange={setRange} />
      </FilterRow>


      <TimeTrackingEntriesTable entries={entries} />
    </PageLayout>
  )
}
